import type { AnyAnalytixEvent } from '~/features/analytix'
import type { AnalytixParams } from '~/shared/analytix'

import { attach, createEvent, createStore, sample } from 'effector'
import { createAction } from 'effector-action'
import { AnalytixDispatcher, AnalytixEmitter } from '~/features/analytix'
import { Analytix } from '~/shared/analytix'
import { logger } from './lib'

/**
 * Runtime configuration entrypoint for the shared Analytix adapter.
 */
export const initialize = createEvent<AnalytixParams>()

/**
 * Enables emitting events to the backend.
 */
export const enable = createEvent()

/**
 * Disables emitting events to the backend.
 */
export const disable = createEvent()

//
// Manage Analytix instance
//

/**
 * Singleton shared adapter instance.
 */
export const $analytix = createStore(new Analytix())

const initializeFx = attach({
  source: $analytix,
  effect: (analytix, params: AnalytixParams) => analytix.initialize(params),
})

const enableFx = attach({
  source: $analytix,
  effect: (analytix) => analytix.enable(),
})

const disableFx = attach({
  source: $analytix,
  effect: (analytix) => analytix.disable(),
})

sample({
  clock: initialize,
  target: initializeFx,
})

sample({
  clock: enable,
  target: enableFx,
})

sample({
  clock: disable,
  target: disableFx,
})

sample({
  clock: initializeFx.failData,
  fn: (fail) => ['failed to initialize analytix:', fail],
  target: logger.errorFx,
})

//
// Events dispatcher and emitter
//

/**
 * Queue enqueue API used by process wires to publish feature events.
 */
export const emit = createEvent<AnyAnalytixEvent>()

/**
 * Holds dispatch queue orchestrator.
 */
export const $dispatcher = createStore<AnalytixDispatcher | null>(null)

/**
 * Holds async emitter loop bound to current adapter instance.
 */
export const $emitter = createStore<AnalytixEmitter | null>(null)

const startFx = attach({
  source: { emitter: $emitter, analytix: $analytix },
  effect: ({ emitter, analytix }) => emitter?.start(analytix),
})

const stopFx = attach({
  source: $emitter,
  effect: (emitter) => emitter?.stop(),
})

const emitFx = attach({
  source: $dispatcher,
  effect: (dispatcher, event) => dispatcher?.add(event),
})

createAction(initialize, {
  target: { $dispatcher, $emitter },
  fn: (target) => {
    const dispatcher = new AnalytixDispatcher()
    const emitter = new AnalytixEmitter(dispatcher)
    target.$dispatcher(dispatcher)
    target.$emitter(emitter)
  },
})

sample({
  clock: enable,
  target: startFx,
})

sample({
  clock: disable,
  target: stopFx,
})

sample({
  clock: emit,
  target: emitFx,
})
